import { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import {
    Box,
    Button,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { Tune as TuneIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useFormContext } from 'react-hook-form';
import NgpTextField from '../../form/NgpTextField';
import { DEFAULT_INPUT_PROPS } from '../../../utilities/FormUtilities';

// ─── Data ────────────────────────────────────────────────────────────────────

const BROWSERS = [
    { key: 'ngenius', label: 'Edge Sensor' },
    { key: 'chrome', label: 'Chrome' },
    { key: 'firefox', label: 'Firefox' },
    { key: 'safari', label: 'Safari' },
    { key: 'edge', label: 'Edge' },
];

const OS_LIST = [
    { key: 'windows', label: 'Windows' },
    { key: 'macos', label: 'macOS' },
    { key: 'linux', label: 'Linux' },
    { key: 'android', label: 'Android' },
    { key: 'ios', label: 'iOS' },
];

const ARCH_LIST = [
    { key: 'x86_64', label: 'x86_64 (64-bit)' },
    { key: 'arm64', label: 'ARM64' },
    { key: 'x86', label: 'x86 (32-bit)' },
];

/** OS keys supported by each browser */
const BROWSER_OS_COMPAT = {
    ngenius: [],
    chrome: ['windows', 'macos', 'linux', 'android', 'ios'],
    firefox: ['windows', 'macos', 'linux', 'android'],
    safari: ['macos', 'ios'],
    edge: ['windows', 'macos', 'linux', 'android', 'ios'],
};

/** Arch keys supported by each OS */
const OS_ARCH_COMPAT = {
    windows: ['x86_64', 'arm64', 'x86'],
    macos: ['x86_64', 'arm64'],
    linux: ['x86_64', 'arm64'],
    android: ['x86_64', 'arm64'],
    ios: ['arm64'],
};

/** Default browser versions */
const DEFAULT_VERSIONS = {
    chrome: '120.0.0.0',
    firefox: '120.0',
    safari: '17.0',
    edge: '120.0.0.0',
};

/** Build the platform token embedded in the UA string */
function buildPlatform(os, arch) {
    switch (os) {
        case 'windows':
            if (arch === 'x86_64') return 'Windows NT 10.0; Win64; x64';
            if (arch === 'arm64') return 'Windows NT 10.0; Win64; ARM64';
            return 'Windows NT 10.0'; // x86
        case 'macos':
            return arch === 'arm64' ? 'Macintosh; Apple Silicon Mac OS X 10_15_7' : 'Macintosh; Intel Mac OS X 10_15_7';
        case 'linux':
            return arch === 'arm64' ? 'X11; Linux aarch64' : 'X11; Linux x86_64';
        case 'android':
            return arch === 'x86_64' ? 'Linux; Android 14; x86_64' : 'Linux; Android 14';
        case 'ios':
            return 'iPhone; CPU iPhone OS 17_0 like Mac OS X';
        default:
            return '';
    }
}

/** Compose the full UA string from the selections */
function buildUserAgent(browser, os, arch, version) {
    if (browser === 'ngenius') return 'ngenius_pulse_test_robot';

    const platform = buildPlatform(os, arch);
    const mobile = os === 'ios' || os === 'android';
    const ver = version || DEFAULT_VERSIONS[browser] || '';

    switch (browser) {
        case 'chrome':
            return `Mozilla/5.0 (${platform}) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${ver}${
                mobile ? ' Mobile' : ''
            } Safari/537.36`;
        case 'firefox':
            return `Mozilla/5.0 (${platform}; rv:${ver}) Gecko/20100101 Firefox/${ver}`;
        case 'safari':
            return os === 'ios'
                ? `Mozilla/5.0 (${platform}) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/${ver} Mobile/15E148 Safari/604.1`
                : `Mozilla/5.0 (${platform}) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/${ver} Safari/605.1.15`;
        case 'edge':
            return `Mozilla/5.0 (${platform}) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${ver}${
                mobile ? ' Mobile' : ''
            } Safari/537.36 Edg/${ver}`;
        default:
            return '';
    }
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    popoverContent: {
        padding: theme.spacing(2),
        width: 680,
        boxSizing: 'border-box',
    },
    title: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(2),
    },
    dropdownsRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    formControl: {
        flex: 1,
        minWidth: 0,
    },
    versionField: {
        flex: 1,
        minWidth: 0,
    },
    preview: {
        backgroundColor: theme.palette.action.hover,
        borderRadius: 4,
        padding: theme.spacing(1),
        marginBottom: theme.spacing(2),
        wordBreak: 'break-all',
        fontFamily: 'monospace',
        fontSize: 10,
        fontStyle: 'italic',
        color: theme.palette.text.secondary,
    },
    previewLabel: {
        fontSize: 11,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
    builderButton: {
        padding: 4,
    },
}));

// ─── Components ───────────────────────────────────────────────────────────────

/**
 * Popover with three linked dropdowns (Browser / OS / Architecture) that
 * compose a User-Agent string. Only compatible OS and arch options are shown
 * for the selected browser and OS respectively.
 *
 * @param {{ onApply: (ua: string) => void }} props
 */
export function RequestHeaderBuilderPopover({ onApply }) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);

    // Initialize to Chrome / Windows / x86_64 defaults
    const [browser, setBrowser] = useState('chrome');
    const [os, setOs] = useState('windows');
    const [arch, setArch] = useState('x86_64');
    const [version, setVersion] = useState('120.0.0.0');

    const open = Boolean(anchorEl);

    // Derived state
    const noOsArch = browser === 'ngenius';
    const compatOs = OS_LIST.filter((o) => BROWSER_OS_COMPAT[browser]?.includes(o.key));
    const compatArch = os ? ARCH_LIST.filter((a) => OS_ARCH_COMPAT[os]?.includes(a.key)) : [];
    const preview = buildUserAgent(browser, os, arch, version);

    const handleOpen = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        // Last selections are kept in state so the next open restores them
    };

    const handleBrowserChange = (value) => {
        setBrowser(value);
        const firstOs = BROWSER_OS_COMPAT[value]?.[0] || '';
        setOs(firstOs);
        const firstArch = firstOs ? OS_ARCH_COMPAT[firstOs]?.[0] || '' : '';
        setArch(firstArch);
        setVersion(DEFAULT_VERSIONS[value] || '');
    };

    const handleOsChange = (value) => {
        setOs(value);
        setArch(OS_ARCH_COMPAT[value]?.[0] || '');
    };

    const handleApply = () => {
        onApply(preview);
        handleClose();
    };

    return (
        <>
            <Tooltip title={t`Build User-Agent`}>
                <IconButton className={classes.builderButton} onClick={handleOpen} size="small">
                    <TuneIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box className={classes.popoverContent}>
                    <Typography className={classes.title}>
                        <Trans>Build User-Agent</Trans>
                    </Typography>

                    {/* Three dropdowns always on one row */}
                    <Box className={classes.dropdownsRow}>
                        {/* Browser */}
                        <FormControl className={classes.formControl}>
                            <InputLabel shrink>
                                <Trans>Browser</Trans>
                            </InputLabel>
                            <Select value={browser} onChange={(e) => handleBrowserChange(e.target.value)}>
                                {BROWSERS.map((b) => (
                                    <MenuItem key={b.key} value={b.key}>
                                        {b.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* OS */}
                        <FormControl className={classes.formControl} disabled={noOsArch}>
                            <InputLabel shrink>
                                <Trans>OS</Trans>
                            </InputLabel>
                            <Select value={os} onChange={(e) => handleOsChange(e.target.value)} displayEmpty>
                                {noOsArch ? (
                                    <MenuItem value="">
                                        <Trans>N/A</Trans>
                                    </MenuItem>
                                ) : (
                                    compatOs.map((o) => (
                                        <MenuItem key={o.key} value={o.key}>
                                            {o.label}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        {/* Architecture */}
                        <FormControl className={classes.formControl} disabled={noOsArch || compatArch.length <= 1}>
                            <InputLabel shrink>
                                <Trans>Architecture</Trans>
                            </InputLabel>
                            <Select value={arch} onChange={(e) => setArch(e.target.value)} displayEmpty>
                                {noOsArch ? (
                                    <MenuItem value="">
                                        <Trans>N/A</Trans>
                                    </MenuItem>
                                ) : (
                                    compatArch.map((a) => (
                                        <MenuItem key={a.key} value={a.key}>
                                            {a.label}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        {/* Version */}
                        <TextField
                            className={classes.versionField}
                            label={<Trans>Version</Trans>}
                            value={version}
                            onChange={(e) => setVersion(e.target.value)}
                            disabled={noOsArch}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ maxLength: 20 }}
                        />
                    </Box>

                    {/* Live preview */}
                    <Typography className={classes.previewLabel}>
                        <Trans>Preview</Trans>
                    </Typography>
                    <Box className={classes.preview}>{preview}</Box>

                    <Box className={classes.actions}>
                        <Button onClick={handleClose}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleApply}>
                            <Trans>Apply</Trans>
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}

/**
 * Header value field that shows a User-Agent builder button when the corresponding
 * header name field is "User-Agent". Other headers render a plain value field.
 *
 * @param {{ index: number, fieldNamePrefix: string, label: import('react').ReactNode, initialName: string }} props
 */
export function RequestHeaderValueField({ index, fieldNamePrefix, label, initialName }) {
    const { setValue, watch } = useFormContext();
    // Use initialName (from field array data) as fallback so the builder icon
    // is visible on first render before the Controller has registered the field.
    const headerName = watch(`${fieldNamePrefix}[${index}].name`, initialName);
    const isUserAgent = String(headerName || '').toLowerCase() === 'user-agent';
    const valueFieldName = `${fieldNamePrefix}[${index}].value`;

    const handleApply = (ua) => {
        setValue(valueFieldName, ua, { shouldDirty: true });
    };

    return (
        <NgpTextField
            fieldName={valueFieldName}
            label={label}
            inputProps={DEFAULT_INPUT_PROPS}
            fullWidth
            /* eslint-disable-next-line react/jsx-no-duplicate-props */
            InputProps={
                isUserAgent
                    ? {
                          endAdornment: (
                              <InputAdornment position="end">
                                  <RequestHeaderBuilderPopover onApply={handleApply} />
                              </InputAdornment>
                          ),
                      }
                    : undefined
            }
        />
    );
}
